import { useEffect, useMemo, useState } from "react";
import { Box, Modal, Typography } from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/Common/Button";
import FilterForm from "./FilterForm";
import UpdateSurveyStatus from "./UpdateSurveyStatus";
import {
  createSurveyFiling,
  getPanelMembers,
  getPersonDisplaySettings,
  getSurveyNames,
  setPersonDisplaySettings,
} from "../Survey/Survey.service";
import {
  setFilterFormData,
  setSelectedPanelMemberData,
} from "../Survey/Survey.slice";
import {
  formatDate,
  getTextFromHTML,
  USER_ROLES,
} from "../../Utility/constants";
import Tooltip from "../../components/Common/Tooltip";
import { initialFormData, filterEmptyFormData } from "./FilterForm";
import LoadingText from "../../components/Common/LoadingText";
import theme from "../../theme";

const ProcessSurvey = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState([]);
  const [panelMembers, setPanelMembers] = useState([]);
  const [surveyNames, setSurveyNames] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [sortBy, setSortBy] = useState(null);
  const [sortAscending, setSortAscending] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [showLoading, setShowLoading] = useState(true);
  const [disableOpde, setDisableOpde] = useState(false);
  const initialColumns = {
    sr_custom_id: true,
    name: true,
    username: true,
    custom_id: true,
    orgName: true,
    county: true,
    status: true,
    stats: true,
    view: true,
    edit: true,
    opde: true,
    admin: true,
    print: true,
  };

  const [openSurveyAdminModal, setOpenSurveyAdminModal] = useState(false);
  const selectedPanelId = useSelector(
    (state) => state?.survey?.selectedPanelId
  );

  const surveyPanels = useSelector((state) => state?.survey?.surveyPanels);
  const filterFormData = useSelector((state) => state?.survey?.filterFormData);
  const orgId = useSelector(
    (state) => state?.survey?.loggedInUserData?.authorizedUser?.organization?.id
  );
  const selectedPanel = Array.isArray(surveyPanels)
    ? surveyPanels.find((panel) => panel.id === selectedPanelId)
    : [];

  const location = useLocation();
  const surveyId = location?.state?.surveyId;
  const userName = useSelector(
    (state) => state?.survey?.loggedInUserData?.authorizedUser?.name
  );
  const personId = useSelector(
    (state) => state?.survey?.loggedInUserData?.authorizedUser?.id
  );

  const roleName = useSelector(
    (state) => state?.survey?.loggedInUserData?.authorizedUser?.role?.name
  );
  const isBuilderGroupUser =
    roleName === USER_ROLES.BUILDER_AHA_GROUP_USER_ROLENAME ? true : false;

  const processFilters = () => {
    const selectedFormData = {};

    Object.keys(filterFormData).forEach((key) => {
      if (Array.isArray(filterFormData[key])) {
        if (filterFormData[key].length > 0) {
          selectedFormData[key] = filterFormData[key].join(",");
        }
      } else if (filterFormData[key] !== "" && filterFormData[key] !== false) {
        selectedFormData[key] = filterFormData[key];
      }
    });
    return selectedFormData;
  };

  useEffect(() => {
    const getDisplaySettings = async () => {
      try {
        const res = await getPersonDisplaySettings({
          personId: personId,
          page: "ProcessSurvey",
        });
        if (Array.isArray(res?.data) && res?.data?.length > 0) {
          const fetchedColumnObj = res.data
            .map((item) => {
              if (item?.type === "column") {
                return item?.name;
              } else return null;
            })
            .reduce((obj, columnName) => {
              if (columnName) {
                obj[columnName] = true;
              }
              return obj;
            }, {});

          for (const key in initialColumns) {
            if (!fetchedColumnObj.hasOwnProperty(key)) {
              fetchedColumnObj[key] = false;
            }
          }

          const fetchedFilterObj = await res.data.reduce((obj, item) => {
            if (item.type === "filter") {
              if (
                [
                  "survey_filing.status_id",
                  "survey_respondent.role_id",
                  "survey_organization.state_id",
                  "survey_respondent.state_id",
                ].includes(item.name)
              ) {
                obj[item.name] = item.value.split(",").map((v) => parseInt(v));
              } else {
                obj[item.name] = item.value;
              }
            }
            return obj;
          }, {});
          setColumnVisibility(fetchedColumnObj);
          if (
            JSON.stringify(fetchedFilterObj) ===
              JSON.stringify(initialFormData) ||
            JSON.stringify(fetchedFilterObj) === JSON.stringify({})
          ) {
            dispatch(setFilterFormData(filterEmptyFormData(initialFormData)));
          } else dispatch(setFilterFormData(fetchedFilterObj));
        } else setColumnVisibility(initialColumns);
      } catch (error) {
        console.error("Error fetching person display settings:", error);
      }
    };
    if (personId) {
      getDisplaySettings();
    }
  }, [personId]);

  useEffect(() => {
    const setColumns = Object.entries(columnVisibility)
      .filter(([key, value]) => value === true)
      .map(([key, value]) => key);

    const setDisplaySettings = async () => {
      try {
        await setPersonDisplaySettings({
          personId: personId,
          page: "ProcessSurvey",
          columns: setColumns,
          filterCriteria: processFilters(),
          createdBy: userName,
        });
      } catch (error) {
        console.error("Error setting person column settings:", error);
      }
    };
    if (Object.keys(columnVisibility).length !== 0) {
      setDisplaySettings();
    }
  }, [columnVisibility, filterFormData]);

  useEffect(() => {
    const panelMember = panelMembers.find(
      (member) => member?.id === selectedMemberId
    );
    if (panelMember) {
      dispatch(setSelectedPanelMemberData(panelMember));
      setOpenSurveyAdminModal(true);
    }
  }, [selectedMemberId]);

  const fetchPanelAssignmentInfo = async () => {
    try {
      const res = await getPanelMembers({
        surveyPanelId: selectedPanelId,
        pageNumber: pagination.pageIndex + 1,
        numberPerPage: pagination.pageSize,
        sortBy: sortBy,
        sortAscending: sortAscending,
        filterCriteria: processFilters(),
        surveyId: surveyId,
      });
      if (res?.data) {
        setPanelMembers(res?.data?.panelMemberAssignments);
        setRowCount(res?.data?.totalNumber);
        setShowLoading(false);
      } else setPanelMembers([]);
    } catch (error) {
      console.error("Error fetching panel members:", error);
    }
  };

  const fetchSurveyNames = async () => {
    try {
      const res = await getSurveyNames(selectedPanelId);
      setSurveyNames(res.data);
    } catch (error) {
      console.error("Error fetching survey names:", error);
    }
  };

  useEffect(() => {
    if (selectedPanelId) {
      fetchSurveyNames();
    }
  }, [selectedPanelId]);

  useEffect(() => {
    if (selectedPanelId) {
      fetchPanelAssignmentInfo();
    }
  }, [
    selectedPanelId,
    filterFormData,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  useEffect(() => {
    if (sorting[0]) {
      if (sorting[0]?.id === "orgName") {
        setSortBy(`survey_organization.name`);
      } else if (sorting[0]?.id === "custom_id") {
        setSortBy(`survey_organization.custom_id`);
      } else if (sorting[0]?.id === "status") {
        setSortBy(`surveyFilingStatus`);
      } else {
        setSortBy(`survey_respondent.${sorting[0]?.id}`);
      }
      setSortAscending(!sorting[0]?.desc);
    }
  }, [sorting]);

  useEffect(() => {
    if (sortBy !== null && sortAscending !== null) {
      fetchPanelAssignmentInfo();
    }
  }, [sortBy, sortAscending]);

  const data = useMemo(() => {
    return panelMembers?.map((member) => {
      return {
        sr_custom_id: member?.surveyRespondent?.srCustomId,
        name: member?.surveyRespondent?.name,
        username: member?.surveyRespondent?.username,
        custom_id: member?.surveyRespondent?.surveyOrganization?.customId,
        orgName: member?.surveyRespondent?.surveyOrganization?.name,
        county: member?.surveyRespondent?.surveyOrganization?.county,
        status: member?.surveyFilingId
          ? member?.surveyFilingStatus
          : "Hasn't Yet Logged In",
        surveyFilingId: member?.surveyFilingId,
        srId: member?.surveyRespondent?.id,
        id: member?.id,
      };
    });
  }, [panelMembers]);

  const createNewSurveyFiling = async (surveyId, srId) => {
    try {
      const response = await createSurveyFiling({
        surveyId: surveyId,
        surveyRespondentId: srId,
      });
      const newSurveyFilingId = await response?.data?.id;
      return newSurveyFilingId;
    } catch (error) {
      console.log("Error creating Survey filing", error);
      return null;
    }
  };

  const handleOpdeClick = (surveyFilingId, srId) => {
    if (surveyFilingId === 0) setDisableOpde(true);
    openTaker("opde", surveyFilingId, srId);

    if (surveyFilingId === 0) {
      // Keep the button disabled for 10 seconds to prevent multiple clicks and thus multiple filings getting created
      setTimeout(() => {
        setDisableOpde(false);
      }, 10000);
    }
  };

  const openTaker = async (page, surveyFilingId, srId) => {
    let baseUrl;
    if (page === "opde" && orgId && srId) {
      if (surveyFilingId === 0) {
        const newSurveyFilingId = await createNewSurveyFiling(surveyId, srId);
        if (newSurveyFilingId) {
          baseUrl = `${process.env.REACT_APP_REACT_TAKER_URL_REDIRECT}/${page}/${newSurveyFilingId}/${orgId}`;
          fetchPanelAssignmentInfo();
        }
      } else
        baseUrl = `${process.env.REACT_APP_REACT_TAKER_URL_REDIRECT}/${page}/${surveyFilingId}/${orgId}`;
    } else
      baseUrl = `${process.env.REACT_APP_REACT_TAKER_URL_REDIRECT}/${page}/${surveyFilingId}`;

    baseUrl += `?isGroupUser=${isBuilderGroupUser}`;
    window.open(baseUrl, "_blank");
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "sr_custom_id",
        header: "Id",
        size: 150,
        enableHiding: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 150,
      },
      {
        accessorKey: "username",
        header: "Username",
        size: 150,
      },
      {
        accessorKey: "custom_id",
        header: "Custom Id",
        size: 150,
      },
      {
        accessorKey: "orgName",
        header: "Org. Name",
        size: 150,
      },
      {
        accessorKey: "county",
        header: "County",
        size: 150,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 150,
      },
      {
        accessorKey: "stats",
        header: "Stats",
        size: 50,
        Cell: ({ value }) => (
          <Tooltip title="This feature is coming soon" arrow>
            <AssessmentOutlinedIcon
              sx={{
                cursor: "pointer",
                width: 40,
                height: 40,
                color: theme.palette.blue.main,
                background: theme.palette.blue.light,
                borderRadius: "10px",
                padding: "10px",
              }}
              // onClick={() => navigate("/survey-info")}
            />
          </Tooltip>
        ),
      },
      {
        accessorKey: "view",
        header: "View",
        size: 50,
        Cell: ({ row }) => (
          <>
            {!!row?.original?.surveyFilingId && (
              <Tooltip title="View data" arrow>
                <DescriptionOutlinedIcon
                  sx={{
                    cursor: "pointer",
                    width: 40,
                    height: 40,
                    color: theme.palette.yellow.main,
                    background: theme.palette.yellow.light,
                    borderRadius: "10px",
                    padding: "10px",
                    marginRight: "10px",
                  }}
                  onClick={() =>
                    openTaker("view", row?.original?.surveyFilingId)
                  }
                />
              </Tooltip>
            )}
          </>
        ),
      },
      {
        accessorKey: "edit",
        header: "Edit",
        size: 50,
        Cell: ({ row }) => (
          <>
            {!!row?.original?.surveyFilingId && (
              <Tooltip
                title={
                  isBuilderGroupUser
                    ? "You do not have access to this function."
                    : "Survey Processing"
                }
                arrow
              >
                <EditOutlinedIcon
                  sx={{
                    cursor: isBuilderGroupUser ? "not-allowed" : "pointer",
                    width: 40,
                    height: 40,
                    color: theme.palette.green.main,
                    background: theme.palette.green.light,
                    borderRadius: "10px",
                    padding: "10px",
                  }}
                  onClick={() => {
                    if (!isBuilderGroupUser) {
                      openTaker("edit", row?.original?.surveyFilingId);
                    }
                  }}
                />
              </Tooltip>
            )}
          </>
        ),
      },
      {
        accessorKey: "opde",
        header: "Opde",
        size: 50,
        Cell: ({ row }) => (
          <Tooltip
            title={
              isBuilderGroupUser
                ? "You do not have access to this function."
                : disableOpde ? "Please wait while a filing is created from the last click" : "Opde"
            }
            arrow
          >
            <NoteAltOutlinedIcon
              sx={{
                cursor: isBuilderGroupUser ? "not-allowed" : "pointer",
                width: 40,
                height: 40,
                color: theme.palette.orange.main,
                background: theme.palette.orange.light,
                borderRadius: "10px",
                padding: "10px",
                marginRight: "10px",
                opacity: disableOpde ? 0.5 : 1,
              }}
              onClick={() => {
                if (!isBuilderGroupUser) {
                  if ( !disableOpde ) {
                    handleOpdeClick(row?.original?.surveyFilingId, row?.original?.srId);
                  }
                }
              }}
            />
          </Tooltip>
        ),
      },
      {
        accessorKey: "admin",
        header: "Admin",
        size: 50,
        Cell: ({ row }) => (
          <>
            {!!row?.original?.surveyFilingId && (
              <Tooltip
                title={
                  isBuilderGroupUser
                    ? "You do not have access to this function."
                    : "Change survey filing status"
                }
                arrow
              >
                <GroupOutlinedIcon
                  sx={{
                    cursor: isBuilderGroupUser ? "not-allowed" : "pointer",
                    width: 40,
                    height: 40,
                    color: theme.palette.purple.main,
                    background: theme.palette.purple.light,
                    borderRadius: "10px",
                    padding: "10px",
                    marginRight: "10px",
                  }}
                  onClick={() => {
                    if (!isBuilderGroupUser) {
                      setSelectedMemberId(row?.original?.id);
                    }
                  }}
                />
              </Tooltip>
            )}
          </>
        ),
      },
      {
        accessorKey: "print",
        header: "Print",
        size: 50,
        Cell: ({ row }) => (
            <>
              {!!row?.original?.surveyFilingId && (
                  <Tooltip title="Print" arrow>
                    <LocalPrintshopOutlinedIcon
                        sx={{
                          cursor: "pointer",
                          width: 40,
                          height: 40,
                          color: theme.palette.pink.main,
                          background: theme.palette.pink.light,
                          borderRadius: "10px",
                          padding: "10px",
                          marginRight: "10px",
                        }}
                        onClick={() =>
                            openTaker("print-survey", row?.original?.surveyFilingId)
                        }
                    />
                  </Tooltip>
              )}
            </>
        ),
      },
    ],
    [disableOpde]
  );

  const table = useMaterialReactTable({
    manualPagination: true,
    manualSorting: true,
    columns,
    data,
    onPaginationChange: setPagination,
    state: { pagination, sorting, columnVisibility },
    onSortingChange: setSorting,
    rowCount,
    onColumnVisibilityChange: setColumnVisibility,
    initialState: { density: "compact" },
    enableColumnFilters: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
  });

  const handleOpenModal = async () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };
  const handleCloseSurveyAdminModal = () => {
    setOpenSurveyAdminModal(false);
    setSelectedMemberId(null);
    fetchPanelAssignmentInfo();
  };
  return showLoading ? (
    <LoadingText />
  ) : (
    <div style={{ padding: "30px" }}>
      <div style={{ marginBottom: "20px" }}>
        <Typography color="primary">
          <strong> Panel:</strong> {selectedPanel?.name} <br />
          <strong> Panel used in: </strong>
          {surveyNames?.map((name) => getTextFromHTML(name))?.join(", ")}
          <br />
          <strong> Modify Date:</strong>
          {formatDate(selectedPanel?.dateModified)}
        </Typography>
      </div>

      <div style={{ marginBottom: "20px" }}></div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "20px",
        }}
      >
        <Button startIcon={<TuneOutlinedIcon />} onClick={handleOpenModal}>
          Set Filter (find) Criteria
        </Button>
      </div>
      <MaterialReactTable table={table} />
      <Modal open={openModal} onClose={handleCloseModal}>
        <FilterForm handleClose={handleCloseModal} />
      </Modal>
      <Modal open={openSurveyAdminModal} onClose={handleCloseSurveyAdminModal}>
        <UpdateSurveyStatus
          handleCloseSurveyAdmin={handleCloseSurveyAdminModal}
        />
      </Modal>
    </div>
  );
};

export default ProcessSurvey;
